import { useState, useEffect, useCallback } from 'react';
import { hashtagsAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Hash, Plus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Sélecteur de hashtags # pour un dossier, PAR MODULE (easy_power / chantier).
 * Tout utilisateur peut taguer ; les # disponibles sont créés par les admins.
 */
export function HashtagPicker({ prospectId, module, className = '' }) {
    const [catalog, setCatalog] = useState([]);
    const [selected, setSelected] = useState([]); // hashtag ids du module
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [cat, mine] = await Promise.all([
                hashtagsAPI.list(),
                hashtagsAPI.getForProspect(prospectId),
            ]);
            setCatalog(cat.data || []);
            setSelected((mine.data?.[module] || []).map(h => h.id));
        } catch { /* silencieux */ }
        finally { setLoading(false); }
    }, [prospectId, module]);

    useEffect(() => { load(); }, [load]);

    const persist = async (ids) => {
        setSaving(true);
        const prev = selected;
        setSelected(ids);
        try {
            await hashtagsAPI.setForProspect(prospectId, module, ids);
        } catch (e) {
            setSelected(prev);
            toast.error("Impossible d'enregistrer les hashtags");
        } finally { setSaving(false); }
    };

    const toggle = (id) => {
        const ids = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
        persist(ids);
    };

    const selectedTags = catalog.filter(h => selected.includes(h.id));

    if (loading) return <div className={className}><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>;

    return (
        <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid={`hashtag-picker-${module}`}>
            {selectedTags.map(h => (
                <Badge key={h.id} style={{ backgroundColor: h.color, color: '#fff' }}
                    className="gap-0.5 border-0 text-[11px]" data-testid={`hashtag-chip-${h.id}`}>
                    <Hash className="w-3 h-3" />{h.label}
                </Badge>
            ))}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1 border-dashed"
                        data-testid={`hashtag-edit-${module}`}>
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        {selectedTags.length === 0 ? 'Ajouter #' : 'Modifier'}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-2" align="start">
                    <p className="px-1 pb-1.5 text-xs font-medium text-slate-500">Hashtags — {module === 'chantier' ? 'Travaux d’optimisation' : 'Courtage'}</p>
                    {catalog.length === 0 ? (
                        <p className="px-1 py-2 text-xs text-slate-400">Aucun hashtag disponible. Un admin doit en créer dans Paramètres.</p>
                    ) : (
                        <div className="max-h-56 overflow-y-auto space-y-0.5">
                            {catalog.map(h => {
                                const on = selected.includes(h.id);
                                return (
                                    <button key={h.id} type="button" onClick={() => toggle(h.id)}
                                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50"
                                        data-testid={`hashtag-option-${h.id}`}>
                                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: h.color }} />
                                        <span className="flex-1 text-left">#{h.label}</span>
                                        {on && <Check className="w-4 h-4 text-green-600" />}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}
